import { useEffect, useState } from "react";
import { Save, Zap } from "lucide-react";
import { ApiError } from "@/lib/api-client";
import { Alert, Badge, Button, Card, CardContent, CardFooter, Field, Input, PageHeader, Select, Spinner, Textarea } from "@/components/ui";
import { useAuthStore } from "@/stores/auth-store";
import { useLlmConfig, useUpsertLlmConfig, useTestLlm } from "./hooks";

const PROVIDERS = ["ARK", "OPENAI", "GOOGLE", "OLLAMA"] as const;

export function LLMConfigPage() {
  const me = useAuthStore((s) => s.user);
  const isSuper = me?.role === "super_admin";
  const [scope, setScope] = useState<string | undefined>(undefined);

  const { data, isLoading } = useLlmConfig(scope);
  const upsert = useUpsertLlmConfig(scope);
  const test = useTestLlm(scope);

  const [provider, setProvider] = useState("ARK");
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [extraJson, setExtraJson] = useState("{}");
  /** apiKey: undefined=保留原值不动, ""=用户主动清空, 非空=新值 */
  const [apiKey, setApiKey] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data?.data) {
      setProvider(data.data.provider || "ARK");
      setModel(data.data.model || "");
      setBaseUrl(data.data.base_url || "");
      setExtraJson(data.data.extra_json || "{}");
      setApiKey(undefined);
    }
  }, [data]);

  const handleSave = async () => {
    setErr(null);
    setOkMsg(null);
    try {
      JSON.parse(extraJson || "{}");
    } catch (e) {
      setErr(`extra_json 不是合法 JSON: ${e instanceof Error ? e.message : ""}`);
      return;
    }
    if (!provider || !model.trim()) {
      setErr("provider 和 model 必填");
      return;
    }
    try {
      await upsert.mutateAsync({
        provider,
        model: model.trim(),
        api_key: apiKey, // undefined 不会被 JSON.stringify 序列化, 后端理解为"保留"
        base_url: baseUrl.trim() || undefined,
        extra_json: extraJson,
      });
      setOkMsg("保存成功");
      setApiKey(undefined);
    } catch (e) {
      setErr(e instanceof ApiError ? ((e.body as { detail?: string } | null)?.detail || `保存失败 (${e.status})`) : "保存失败");
    }
  };

  const handleTest = async () => {
    setErr(null);
    setOkMsg(null);
    try {
      const r = await test.mutateAsync();
      if (r.success) {
        setOkMsg(`${scope === "global" ? "全局默认配置" : ""}测试成功 (${r.latency_ms}ms): ${r.sample_reply ?? ""}`);
      } else {
        setErr(`测试失败: ${r.error ?? ""}`);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? `测试失败 (${e.status})` : "测试失败");
    }
  };

  const hasKey = data?.data?.has_api_key;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="LLM 配置"
        description="报价与对话使用的大模型服务。保存前可先测试连接。"
        actions={
          isSuper && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={scope === "global"}
                onChange={(e) => {
                  setScope(e.target.checked ? "global" : undefined);
                  setErr(null);
                  setOkMsg(null);
                }}
              />
              管理全局默认 (company_id=0)
            </label>
          )
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> 加载中…</div>
      )}

      <Card>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Provider">
              <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
                {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Model">
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="例: doubao-1.5-pro-32k-250115 / gpt-4o"
                className="font-mono md:text-xs"
              />
            </Field>
          </div>

          <Field label="Base URL" hint="可空">
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="例: https://ark.cn-beijing.volces.com/api/v3"
              className="font-mono md:text-xs"
            />
          </Field>

          <Field
            label="API Key"
            labelAside={hasKey && <Badge variant="success" dot>已设置</Badge>}
            hint={
              hasKey && apiKey === ""
                ? <span className="text-warning-strong">将清空已保存的 key</span>
                : hasKey ? "输入新值可覆盖, 留空保持不变" : undefined
            }
          >
            <Input
              type="password"
              value={apiKey ?? ""}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasKey ? "保持原值留空" : "输入 API Key"}
              autoComplete="off"
              className="font-mono md:text-xs"
            />
          </Field>

          <Field label="extra_json" hint="额外参数">
            <Textarea
              value={extraJson}
              onChange={(e) => setExtraJson(e.target.value)}
              rows={4}
              className="font-mono text-xs md:text-xs"
            />
          </Field>

          {err && <Alert>{err}</Alert>}
          {okMsg && <Alert variant="success">{okMsg}</Alert>}
        </CardContent>

        <CardFooter className="justify-between">
          <Button variant="outline" icon={<Zap />} onClick={handleTest} loading={test.isPending}>
            {test.isPending ? "测试中…" : "测试连接"}
          </Button>
          <Button icon={<Save />} onClick={handleSave} loading={upsert.isPending}>
            {upsert.isPending ? "保存中…" : "保存"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
